import React from 'react';
import classNames from 'classnames';
import Button from 'react/components/button';
import Icon from 'react/components/icon';

type BannerProps = {
  title?: React.ReactNode;
  description: React.ReactNode | string;
  buttonText?: string;
  secondaryButtonText?: string;
  onClick?: () => void;
  onSecondaryClick?: () => void;
  onClose?: () => void;
  href?: string;
  secondaryHref?: string;
  variant?: 'info' | 'success' | 'error' | 'warn';
  buttonVariant?: 'primary--v2' | 'link';
  icon: string;
  noMarginTop?: boolean;
  noMarginBottom?: boolean;
  stackedLayout?: boolean;
};

const Banner = (props: BannerProps) => {
  const {
    title,
    description,
    buttonText,
    secondaryButtonText,
    onClick,
    onSecondaryClick,
    onClose,
    href,
    secondaryHref,
    variant = 'info',
    buttonVariant,
    icon,
    noMarginTop,
    noMarginBottom,
    stackedLayout,
  } = props;

  const isPrimeIcon = icon.startsWith('pi-');

  const getIconColor = () => {
    const colorMap = {
      info: '#2197f3',
      success: '#43b837',
      warn: '#d69c0b',
      error: '#db2121',
    };
    return colorMap[variant];
  };

  const messageClasses = classNames('cw-banner', {
    'cw-banner--no-margin-top': noMarginTop,
    'cw-banner--no-margin-bottom': noMarginBottom,
    'cw-banner--stacked': stackedLayout,
  });

  const renderIcon = () => {
    if (isPrimeIcon) {
      return <span className={`p-message-icon pi ${icon}`}></span>;
    }
    return <Icon name={icon as any} width={18} height={18} color={getIconColor()} />;
  };

  const renderPrimaryAction = () => {
    if (!buttonText) return null;

    if (buttonVariant === 'link') {
      if (href) {
        return <a href={href}>{buttonText}</a>;
      }

      if (onClick) {
        return (
          <Button variant="link" size="xs" onClick={onClick}>
            {buttonText}
          </Button>
        );
      }
    }

    if (href) {
      return <a href={href}>{buttonText}</a>;
    }

    if (onClick) {
      return (
        <Button variant="primary" size="xs" onClick={onClick}>
          {buttonText}
        </Button>
      );
    }

    return null;
  };

  const renderSecondaryAction = () => {
    if (!secondaryButtonText) return null;

    if (secondaryHref) {
      return <a href={secondaryHref}>{secondaryButtonText}</a>;
    }

    if (onSecondaryClick) {
      return (
        <Button variant="link" size="xs" onClick={onSecondaryClick}>
          {secondaryButtonText}
        </Button>
      );
    }

    return null;
  };

  const renderContent = () => {
    if (stackedLayout) {
      return (
        <div className="cw-banner__content-stacked">
          {title && <div className="cw-banner__title">{title}</div>}
          {description && <div className="cw-banner__description">{description}</div>}
          {(buttonText || secondaryButtonText) && (
            <div className="cw-banner__actions">
              {renderPrimaryAction()}
              {renderSecondaryAction()}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="cw-banner__content">
        <span className="cw-banner__text">
          {title && <strong>{title}</strong>}
          {title && description && ' '}
          {description}
        </span>
        {(buttonText || secondaryButtonText) && (
          <div className="cw-banner__actions">
            {renderPrimaryAction()}
            {renderSecondaryAction()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={messageClasses}>
      <div className={`p-message p-message-${variant}`} role="alert">
        <div className="p-message-wrapper">
          {renderIcon()}
          <div className="p-message-text">{renderContent()}</div>
        </div>
      </div>
      {onClose && (
        <button className="cw-banner__close" onClick={onClose} type="button" aria-label="Close">
          <i className="pi pi-times" />
        </button>
      )}
    </div>
  );
};

export default Banner;
